import PortalIdParser from 'PortalIdParser';
import { COMMERCE_PAYMENT_TYPE_ID, DEAL_TYPE_ID, LINE_ITEM_TYPE_ID, PRODUCT_TYPE_ID, QUOTE_TYPE_ID, SUBSCRIPTION_TYPE_ID, TASK_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { LINE_ITEM, PRODUCT } from 'customer-data-objects/constants/ObjectTypes';
import { currencyPricePrefix } from 'customer-data-objects/lineItem/PropertyNames';
import { isPricingModelTiered } from 'customer-data-objects/product/Pricing';
import { EDITABLE_PRODUCT_BUNDLE_PROPERTIES } from 'customer-data-objects/product/isReadOnlyProductProperty';
import { READ_ONLY_QUOTE_PROPERTIES, EDITABLE_QUOTE_STATUS_VALUES } from 'customer-data-objects/quote/constants/properties';
import { isCurrency, isEmail } from 'customer-data-objects/property/PropertyIdentifier';
import enviro from 'enviro';
import { PORTAL_53_PORTALS_OBJECT_TYPE_ID, PORTAL_53_PORTAL_ID_PROPERTY_NAME, PORTAL_53_QA_PORTALS_OBJECT_TYPE_ID } from '../../constants/Portal53';
import { isBETPortal } from './isBETPortal';
import { isManagedQuoteTemplateType } from 'customer-data-objects/quote/constants/quoteTemplateTypes';
import { STATUS, TYPE } from 'customer-data-objects/task/TaskPropertyNames';
export const isEditableDealExchangeRate = (property, objectTypeId) => objectTypeId === DEAL_TYPE_ID && property.name === 'hs_exchange_rate' && !property.calculated;
export const isSettlementCurrencyProperty = (property, objectTypeId) => objectTypeId === COMMERCE_PAYMENT_TYPE_ID && (property.name === 'hs_fees_amount' || property.name === 'hs_platform_fee' || property.name === 'hs_net_amount' || property.name === 'hs_initial_amount' || property.name === 'hs_refunds_amount');

// This is a special case for the BET portal where we want to use the email validation for 'primary_point_of_contact_email' property.
// isEmail will be extended to use an identifier (probably 'validationHint') in property definition
export const isRevenueEmail = (propertyDefinition, objectType) => {
  const BETPortalId = enviro.isQa() ? 99535353 : 53;
  return BETPortalId === PortalIdParser.get() && isEmail(propertyDefinition, objectType);
};

// In PropertyInputV2, for subscription objects, treat hubspotDefined properties as read-only,
// except for hs_name and hubspot_owner_id, which should remain editable.
// See https://hubspot.slack.com/archives/C0779K5FNJU/p1720732734377819
export const isReadOnlyBillableSubscriptionProperty = (objectTypeId, propertyDefinition) => {
  return objectTypeId === SUBSCRIPTION_TYPE_ID && propertyDefinition.hubspotDefined && !['hs_name', 'hubspot_owner_id'].includes(propertyDefinition.name);
};

/**
 * Identifies enum properties that should not display blank options in UI selects,
 * even when not marked as required. Used when we can't mark properties as required
 * but still want to prevent blank selections, and can't use default values.
 */
export const isEnumWithoutBlankOption = (objectTypeId, property) => {
  if (objectTypeId === PRODUCT_TYPE_ID) {
    if (property.name === 'hs_status') {
      return true;
    }
  }
  if (objectTypeId === TASK_TYPE_ID) {
    if (property.name === STATUS || property.name === TYPE) {
      return true;
    }
  }
  return false;
};

/** Products & Line Items Specific Identifiers */
export function isMultiCurrencyPrice(property) {
  return !!(isCurrency(property) && property.hubspotDefined && property.name && property.name.startsWith(currencyPricePrefix));
}
export const isTaxCategoryProperty = (property, objectType) => (objectType === PRODUCT || objectType === LINE_ITEM) && property.name === 'hs_tax_category';
export const isPricingModel = (property, objectTypeId) => property.name === 'hs_pricing_model' && (objectTypeId === PRODUCT_TYPE_ID || objectTypeId === LINE_ITEM_TYPE_ID);
export const isReadOnlyProductProperty = ({
  objectTypeId,
  property,
  pricingModel,
  bundleType
}) => {
  if (objectTypeId !== PRODUCT_TYPE_ID || !property.hubspotDefined) {
    // if objectTypeId is not PRODUCT or it's a custom property, it should be editable
    return false;
  }
  if (bundleType && bundleType === 'open') {
    if (EDITABLE_PRODUCT_BUNDLE_PROPERTIES.has(property.name)) {
      return false;
    }
    return true;
  }
  if (isPricingModel(property, objectTypeId)) {
    return true;
  }
  if (isMultiCurrencyPrice(property) || property.name === 'price') {
    if (isPricingModelTiered(pricingModel)) {
      return true;
    }
    return false;
  }
  return false;
};
export const isPortalIdProperty = (objectTypeId, property) => {
  return isBETPortal() && (objectTypeId === PORTAL_53_PORTALS_OBJECT_TYPE_ID || objectTypeId === PORTAL_53_QA_PORTALS_OBJECT_TYPE_ID) && property.name === PORTAL_53_PORTAL_ID_PROPERTY_NAME;
};
export const isReadOnlyQuoteProperty = ({
  objectTypeId,
  property,
  templateType,
  status,
  locked
}) => {
  if (objectTypeId !== QUOTE_TYPE_ID || !property.hubspotDefined) {
    // if objectTypeId is not QUOTE or it's a custom property, it should be editable
    return false;
  }

  // If the template type is not a managed quote template type, it should be editable
  if (!templateType || !isManagedQuoteTemplateType(templateType)) {
    return false;
  }

  // If the quote is locked, all hubspotDefined properties should be read-only
  if (locked === 'true') {
    return true;
  }

  // If the status is not considered editable, all hubspotDefined properties should be read-only
  if (status && !EDITABLE_QUOTE_STATUS_VALUES.has(status)) {
    return true;
  }
  return READ_ONLY_QUOTE_PROPERTIES.has(property.name);
};